/**
 * The Feedback inbox's "Leadership report" view (8 Oct).
 *
 * Notes and suggestions the IT Leadership Team adds from the Leadership
 * report (`/leadership-report`, "Add a note", 16 Oct), newest first: when,
 * who, what, with the report's dates and filters and the "Ask about this
 * report" question it came from, so Rusty can turn them into backlog items.
 * Stored as feedback rows of their own type, so they never count in the
 * assistant's 👍/👎 figures. Open by default; each is resolved with a fix
 * note, and reopened, like other feedback. The earlier "Ask Rusty" questions
 * (before 16 Oct) still show, with any answer they got, read-only.
 *
 * 12 Oct: one-screen layout — filters on one row, the question and answer
 * clamped with the full text under the row on click, and the answer box one
 * line high with its button beside it.
 *
 * 16 Oct: questions asked in "Ask about this report" are listed too (under
 * "All" only), marked "Asked the report assistant", with the assistant's
 * answer and no answer box.
 */

import { Badge, DataGrid, type DataGridColumn, Typography } from "@norican/react";
import { type ReactNode, useMemo } from "react";

import { EmptyStateBlock, ErrorState, LoadingRows, useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";
import type { LeadershipFeedbackEntry } from "@/lib/api";

import { useAdminLeadershipFeedback } from "./api";
import {
  Clamp,
  COMPACT_GRID_CLASS,
  DetailField,
  DetailPanel,
  ShortDateTime,
} from "./compact-cells";
import {
  EmptyForStatus,
  ResolutionFixCell,
  ResolutionStatusCell,
  StatusFilterChips,
} from "./resolution-controls";
import type { StatusFilter } from "./resolution-helpers";

function answeredBy(
  row: LeadershipFeedbackEntry,
  t: ReturnType<typeof useT>["t"],
  formatDateTime: (value: string) => string,
): string {
  return t("feedbackAdmin.leadership.reply.by", {
    name: row.replied_by ?? "",
    date: row.replied_at ? formatDateTime(row.replied_at) : "",
  });
}

/** What came with an item: a note's dates, filters and question; an answer otherwise. */
function DetailsCell({ row }: { row: LeadershipFeedbackEntry }) {
  const { t } = useT("assistant");
  const { formatDateTime } = useDateFormatter();
  if (row.kind === "ask") {
    // Asked in "Ask about this report" (16 Oct): answered there.
    return (
      <span className="flex min-w-0 flex-col" title={t("feedbackAdmin.leadership.assistantAnswer")}>
        <Clamp>{row.assistant_answer ?? "–"}</Clamp>
      </span>
    );
  }
  if (row.kind === "note") {
    return (
      <span className="flex min-w-0 flex-col">
        <Clamp>{noteContext(row, t)}</Clamp>
      </span>
    );
  }
  return (
    <span className="flex min-w-0 flex-col" title={answeredBy(row, t, formatDateTime)}>
      <Clamp>{row.reply_text ?? "–"}</Clamp>
    </span>
  );
}

/** A note's context in one line: dates and filters, and the question it came from. */
function noteContext(row: LeadershipFeedbackEntry, t: ReturnType<typeof useT>["t"]): string {
  const parts = [t("feedbackAdmin.leadership.scope", { scope: row.scope ?? "–" })];
  if (row.related_question) {
    parts.push(t("feedbackAdmin.leadership.relatedQuestion", { question: row.related_question }));
  }
  return parts.join(" · ");
}

/** The question and its answer, full length: opens under the row on click. */
function LeadershipDetail({ row }: { row: LeadershipFeedbackEntry }) {
  const { t } = useT("assistant");
  const { formatDateTime } = useDateFormatter();
  return (
    <DetailPanel>
      <DetailField label={`${t("feedbackAdmin.leadership.column.comment")} · ${row.user_email}`}>
        {row.comment}
      </DetailField>
      {row.kind === "note" ? (
        <>
          <DetailField label={t("feedbackAdmin.leadership.scopeLabel")}>
            {row.scope ?? "–"}
          </DetailField>
          {row.related_question ? (
            <DetailField label={t("feedbackAdmin.leadership.relatedQuestionLabel")}>
              {row.related_question}
              {row.related_answer ? `\n\n${row.related_answer}` : ""}
            </DetailField>
          ) : null}
        </>
      ) : (
        <DetailField
          label={
            row.kind === "ask"
              ? t("feedbackAdmin.leadership.assistantAnswer")
              : t("feedbackAdmin.leadership.column.reply")
          }
        >
          {row.kind === "ask" ? (
            (row.assistant_answer ?? "–")
          ) : row.reply_text ? (
            <>
              {row.reply_text}
              {"\n"}
              <Typography variant="caption" tone="muted">
                {answeredBy(row, t, formatDateTime)}
              </Typography>
            </>
          ) : (
            "–"
          )}
        </DetailField>
      )}
    </DetailPanel>
  );
}

export interface AdminLeadershipFeedbackProps {
  /** The inbox's view chips, first on this view's filter row. */
  toolbar?: ReactNode;
  /** Open / Resolved / All. */
  status: StatusFilter;
  onStatusChange: (next: StatusFilter) => void;
}

export function AdminLeadershipFeedback({
  toolbar,
  status,
  onStatusChange,
}: AdminLeadershipFeedbackProps) {
  const { t } = useT("assistant");
  const { t: tCommon } = useT("common");
  const list = useAdminLeadershipFeedback({ status });

  const columns = useMemo<DataGridColumn<LeadershipFeedbackEntry>[]>(() => {
    const detail = (row: LeadershipFeedbackEntry) => <LeadershipDetail row={row} />;
    return [
      {
        id: "date",
        header: t("feedbackAdmin.leadership.column.date"),
        width: 96,
        cell: (row) => <ShortDateTime value={row.created_at} />,
      },
      {
        id: "from",
        header: t("feedbackAdmin.leadership.column.from"),
        width: 180,
        cell: (row) => (
          <span className="block truncate" title={row.user_email}>
            {row.user_email}
          </span>
        ),
      },
      {
        id: "section",
        header: t("feedbackAdmin.leadership.column.type"),
        width: 170,
        cell: (row) =>
          row.kind === "ask" ? (
            <Badge tone="info" variant="subtle" size="sm">
              {t("feedbackAdmin.leadership.asked")}
            </Badge>
          ) : row.kind === "note" ? (
            <Badge tone="accent" variant="subtle" size="sm">
              {t("feedbackAdmin.leadership.noteBadge")}
            </Badge>
          ) : row.section ? (
            <Badge tone="neutral" variant="outline" size="sm">
              {tCommon(`leadershipReport.section.${row.section}`)}
            </Badge>
          ) : null,
      },
      {
        id: "comment",
        header: t("feedbackAdmin.leadership.column.comment"),
        width: 300,
        cell: (row) => <Clamp>{row.comment}</Clamp>,
        renderCellDetail: detail,
      },
      {
        id: "reply",
        header: t("feedbackAdmin.leadership.column.details"),
        width: 320,
        cell: (row) => <DetailsCell row={row} />,
      },
      {
        id: "status",
        header: t("feedbackAdmin.resolution.column.status"),
        width: 70,
        align: "center",
        cell: (row) => <ResolutionStatusCell resolution={row.resolution} />,
      },
      {
        id: "fix",
        header: t("feedbackAdmin.resolution.column.fix"),
        width: 170,
        cell: (row) =>
          row.kind === "ask" ? null : (
            <ResolutionFixCell
              kind="leadership"
              itemKey={row.feedback_id}
              resolution={row.resolution}
              subject={row.comment}
            />
          ),
      },
    ];
  }, [t, tCommon]);

  const items = list.data?.items ?? [];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {toolbar}
        <StatusFilterChips
          id="leadership-status-filter"
          value={status}
          counts={list.data?.counts}
          onChange={onStatusChange}
        />
      </div>
      <Typography variant="caption" tone="muted">
        {t("feedbackAdmin.leadership.subtitle")}
      </Typography>
      {list.isLoading ? (
        <LoadingRows rows={5} />
      ) : list.error != null ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <EmptyForStatus status={status} counts={list.data?.counts}>
          <EmptyStateBlock
            title={t("feedbackAdmin.leadership.empty.title")}
            description={t("feedbackAdmin.leadership.empty.hint")}
          />
        </EmptyForStatus>
      ) : (
        <DataGrid
          aria-label={t("feedbackAdmin.leadership.tableLabel")}
          columns={columns}
          data={items}
          getRowId={(row) => row.feedback_id}
          tableLayout="fixed"
          density="compact"
          className={COMPACT_GRID_CLASS}
        />
      )}
    </div>
  );
}
